import * as R from 'ramda';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../../../core/redux/api/circuitBreaker/module';
import { apiGetResortHistory } from '../../../../core/redux/api/endpoints';
import { IAppState } from '../../../../core/redux/IAppState';
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from '../../../../core/redux/api/reduxStandardApiAction';
import { Entitlement } from '../../models';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { assoc } from '../../../../language/module';
import { getResource } from '../../../../language/resources/getResource';


export const KEY_ACTION = ACTION_TYPE.RESORT_HISTORY_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.RESORT_HISTORY_REQUEST,
  ACTION_TYPE.RESORT_HISTORY_RESPONSE,
  ACTION_TYPE.RESORT_HISTORY_ERROR,
];


/**
 * Makes an action that can be dispatched to get a guest's history at a resort from their passes.
 */
export const getResortRedemptionsAction = (
  guestId: string,
  entitlementId: number,
  resortAbbrev: string,
  resortName: string,
) => makeApiAction(
  {
    method:   'GET',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: state => apiGetResortHistory(
      guestId,
      state.resortToolsConfig.dtwCategoryId,
      resortAbbrev,
      state.resortToolsConfig.enableRedemptionDisplayOnly,
      state.selectedResort.resortAbbreviation,
      ),
    types:    ACTIONS.map(action => ({
      type: action,
      meta: { guestId, entitlementId, resortAbbrev, resortName },
    })) as RsaaTypes,
    bailout:  (state: IAppState) => R.path(
      [ 'extras', 'resortAbbrev' ],
      getLoading(state, guestId, KEY_ACTION),
    ) === resortAbbrev || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<Entitlement[]> = (state, action, error, isLoading, payload) => {
  const guestId       = action.meta.guestId;
  const entitlementId = +action.meta.entitlementId;
  const resortAbbrev  = action.meta.resortAbbrev;
  const resortName    = action.meta.resortName;

  // If we're loading, remove the DTW Redemption's errors and add a loading state
  if (isLoading) {
    return addLoading(state, guestId!, KEY_ACTION, { resortAbbrev });
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      guestId!,
      KEY_ACTION,
      error || getResource(state.locale, 'VIEW_DAYS_HISTORY_ERROR'),
      { resortAbbrev },
    );
  }

  // Remove loading/error since we're not loading or erroring
  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  // Results are an array though Patrick said this should be a single-element array 95% of the time
  const history = (payload || []).filter((historyElement => historyElement.entitlementId === entitlementId))[ 0 ];

  if (!history) {
    return noLoadingOrError;
  }

  return assoc(
    'guestResortHistories',
    noLoadingOrError.guestResortHistories.add(history.resortAbbrev, assoc('name', resortName, history)),
    noLoadingOrError,
  );
};


/**
 * Function that maps actions into new app state. Specifically, updates app state with information from actions related
 * to a guest's redemptions at resorts while using a specific pass. Returns a new app state, of course.
 */
export const getResortRedemptionsReducer = makeApiReducer<Entitlement[]>(ACTIONS, KEY_ACTION, reducer);
